import { vereinbarungFuerProfil } from './zw-bware-vereinbarung.js';

/**
 * Zwergenwald - Kaufbereich der B-Ware (blocks/zw-bware-zustand.liquid, Auftrag B-Ware Bettgitter 07.10.2026).
 *
 * Die B-Ware wird nur ueber "Jetzt kaufen" verkauft, nie ueber einen Warenkorb-Knopf (die Abweichung muss mit).
 * Gemischte Warenkoerbe sind trotzdem moeglich: Der Direktlink legt die B-Ware in den Warenkorb der Sitzung; verlaesst
 * die Kundin die Kasse und legt danach Neuware normal dazu, liegt beides drin (rechtstexte/09, Abschnitt B-Ware;
 * Warenkorb-Hinweise: assets/zw-warenkorb.js). Der Knopf ist gesperrt, bis das Pflicht-Haekchen gesetzt ist, und
 * gesperrt, solange die gewaehlte Groesse nicht verfuegbar ist oder ein Groessenwechsel noch laedt (gezaehlt, damit
 * ein schneller Doppelwechsel die Sperre nicht zu frueh aufhebt). Ohne JavaScript bleibt er gesperrt (disabled im HTML).
 *
 * Kaufweg: Warenkorb-Direktlink /cart/<Varianten-ID>:1?properties=<Base64-JSON> - Shopify legt die Eigenschaft
 * "Vereinbarte Abweichung" an die Bestellzeile, sie steht dann in Kasse, Bestellbestaetigung und Bestellung
 * (https://shopify.dev/docs/apps/build/checkout/create-cart-permalinks). Am 07.10.2026 gegen den Live-Warenkorb
 * geprueft (ohne Kasse): Die Eigenschaft kommt mit Umlauten an, Standard-Base64 (URL-kodiert) und Base64url gehen
 * beide, und der Direktlink ERSETZT einen schon gefuellten Warenkorb statt ihn zu ergaenzen.
 *
 * Wie ein kleiner React-Wrapper: Zustand ("Haekchen gesetzt", "Groesse verfuegbar", "offene Wechsel") ergibt
 * zusammen den Wert fuer disabled; die Groesse selbst bleibt bei Horizons Variantenauswahl, der Block liest sie
 * nach jedem Wechsel aus dem neu geladenen HTML (zw-variantenblock.js, Ereignis "zw:variantenblock-aktualisiert").
 */

/**
 * Base64 des JSON (UTF-8), URL-kodiert - die Schreibweise fuer den Parameter "properties".
 * @param {Record<string, string>} eigenschaften
 * @returns {string}
 */
export function kodiereEigenschaften(eigenschaften) {
  const bytes = new TextEncoder().encode(JSON.stringify(eigenschaften));
  let binaer = '';
  for (const b of bytes) binaer += String.fromCharCode(b);
  return encodeURIComponent(btoa(binaer));
}

/**
 * @param {string} warenkorbAdresse - z. B. "/cart"
 * @param {string} variantenId
 * @param {string} name - Name der Eigenschaft
 * @param {string} wert - Wert der Eigenschaft (fester Abweichungstext)
 * @returns {string}
 */
export function baueDirektlink(warenkorbAdresse, variantenId, name, wert) {
  return `${warenkorbAdresse}/${encodeURIComponent(variantenId)}:1?properties=${kodiereEigenschaften({ [name]: wert })}`;
}

const ZW_AKTUALISIERT = 'zw:variantenblock-aktualisiert';

if (typeof HTMLElement !== 'undefined' && typeof customElements !== 'undefined' && !customElements.get('zw-bware-zustand')) {
  class ZwBwareZustand extends HTMLElement {
    /** Zahl der Groessenwechsel, deren Abruf noch laeuft (Horizon bricht einen alten Abruf ab, wenn ein neuer startet). */
    #offen = 0;
    /** @type {string | null} */
    #wahlEreignis = null;

    connectedCallback() {
      this.addEventListener('change', this.#beiAenderung);
      this.addEventListener('click', this.#beiKlick);
      this.addEventListener(ZW_AKTUALISIERT, this.#beiAktualisierung);
      window.addEventListener('pageshow', this.#beiAenderung);
      this.#zustandAnwenden();
      // Der Ereignisname kommt aus Horizons Standard-Ereignissen (wie in zw-variantenblock.js); Import erst hier,
      // damit die reinen Funktionen oben auch ausserhalb des Browsers testbar bleiben.
      import('@shopify/events')
        .then(({ StandardEvents }) => {
          if (!this.isConnected) return;
          this.#wahlEreignis = StandardEvents.productSelect;
          document.addEventListener(this.#wahlEreignis, this.#beiWahl, true);
        })
        .catch(() => {});
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.#beiAenderung);
      this.removeEventListener('click', this.#beiKlick);
      this.removeEventListener(ZW_AKTUALISIERT, this.#beiAktualisierung);
      if (this.#wahlEreignis) document.removeEventListener(this.#wahlEreignis, this.#beiWahl, true);
      this.#wahlEreignis = null;
      window.removeEventListener('pageshow', this.#beiAenderung);
    }

    get #haken() {
      return /** @type {HTMLInputElement | null} */ (this.querySelector('[data-zw-haken]'));
    }

    get #knopf() {
      return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-zw-kaufen]'));
    }

    #beiAenderung = () => this.#zustandAnwenden();

    /** Neues HTML des Knopf-Bereichs ist da (zw-variantenblock.js) - nur neu anwenden, die Sperre zaehlt #offen. */
    #beiAktualisierung = () => this.#zustandAnwenden();

    /** Ein Abruf ist fertig, abgebrochen oder fehlgeschlagen. */
    #erledigt = () => {
      this.#offen = Math.max(0, this.#offen - 1);
      this.#zustandAnwenden();
    };

    /**
     * Horizon meldet jeden Groessenwechsel an das Dokument. Bis die neue Seite da ist, ist der Knopf gesperrt - sonst
     * koennte ein schneller Klick die alte Variante kaufen. Jeder Wechsel zaehlt einzeln: Bricht Horizon den ersten Abruf
     * ab (AbortError), gibt das nur dessen Zaehler frei, nicht den des zweiten. Nach Erfolg 400 ms Puffer fuer das
     * Einsetzen des neuen HTML (zw-variantenblock.js morpht im selben Versprechen).
     * @param {Event} event
     */
    #beiWahl = (event) => {
      const ziel = event.target;
      if (ziel instanceof Element && (ziel.closest('product-card') || ziel.closest('dialog, quick-add-component'))) return;
      const versprechen = /** @type {{ promise?: Promise<unknown> }} */ (event).promise;
      if (!versprechen || typeof versprechen.then !== 'function') return;
      this.#offen += 1;
      this.#zustandAnwenden();
      versprechen.then(
        () => window.setTimeout(this.#erledigt, 400),
        () => this.#erledigt()
      );
    };

    #zustandAnwenden() {
      const knopf = this.#knopf;
      if (!knopf) return;
      const verfuegbar = knopf.dataset.verfuegbar === 'true' && Boolean(knopf.dataset.varianteId);
      const bestaetigt = Boolean(this.#haken?.checked);
      knopf.disabled = !(verfuegbar && bestaetigt && this.#offen === 0);

      const hinweis = this.querySelector('[data-zw-hinweis-haken]');
      if (hinweis instanceof HTMLElement) hinweis.hidden = bestaetigt || !verfuegbar;
    }

    /** @param {Event} event */
    #beiKlick = (event) => {
      const knopf = event.target instanceof Element ? event.target.closest('[data-zw-kaufen]') : null;
      if (!(knopf instanceof HTMLButtonElement) || knopf.disabled) return;
      if (!this.#haken?.checked || this.#offen > 0 || knopf.dataset.verfuegbar !== 'true') return;

      const variante = knopf.dataset.varianteId;
      const vereinbarung = vereinbarungFuerProfil(this.dataset.profil);
      if (!variante || !vereinbarung) return;

      knopf.disabled = true;
      window.location.assign(baueDirektlink(this.dataset.warenkorb || '/cart', variante, vereinbarung.eigenschaft, vereinbarung.abweichung));
    };
  }

  customElements.define('zw-bware-zustand', ZwBwareZustand);
}
